import React from 'react';
import styles from './css/index.module.css';
import { Input, Select, Textarea } from '@fluentui/react-components';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import ReactSelect from 'react-select';
import { resolveWorkspaceProjectTypeSelectOptions } from '../../utils/projectTypes';
import {
    defaultProjectMetadataFormValues,
    PROJECT_SCOPE_SELECT_STYLES,
    resolveWorkspaceCustomerTypeOptions,
    resolveWorkspaceInvestmentTypeOptions,
    resolveWorkspaceScopeAreaOptions,
} from '../../utils/projectMetadataOptions';

const AddProject = ({ compareObjectsValues }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = React.useState({
        project_name: '',
        investor_name: '',
        project_deadline: '',
        project_type: '',
        status: '',
        engagement_objective: '',
        comments: '',
        project_manager: '',
        lead_consultant: '',
        support_consultants: [],
        key_investor_name: '',
        key_investor_email: '',
        ...defaultProjectMetadataFormValues(),
    });

    const { projectDetailButtonLoading, selectedProject } = useSelector(state => state.projectData);
    const { projectUsersAll } = useSelector(state => state.team);
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');

    const activeTenant = React.useMemo(() => {
        const m = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return m?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const projectTypeOptions = React.useMemo(
        () =>
            resolveWorkspaceProjectTypeSelectOptions(activeTenant, {
                includeValue: formData?.project_type || selectedProject?.project_type,
            }),
        [activeTenant, formData?.project_type, selectedProject?.project_type],
    );

    const investmentTypeOptions = React.useMemo(
        () =>
            resolveWorkspaceInvestmentTypeOptions(activeTenant, {
                includeValue: formData?.investment_type || selectedProject?.investment_type,
            }),
        [activeTenant, formData?.investment_type, selectedProject?.investment_type],
    );

    const customerTypeOptions = React.useMemo(
        () =>
            resolveWorkspaceCustomerTypeOptions(activeTenant, {
                includeValue: formData?.customer_type || selectedProject?.customer_type,
            }),
        [activeTenant, formData?.customer_type, selectedProject?.customer_type],
    );

    const scopeAreaOptions = React.useMemo(
        () => resolveWorkspaceScopeAreaOptions(activeTenant, { includeValues: formData?.scope_areas || [] }),
        [activeTenant, formData?.scope_areas],
    );

    const scopeAreaSelectOptions = React.useMemo(
        () => scopeAreaOptions.map(option => ({ value: option.value, label: option.text })),
        [scopeAreaOptions],
    );

    const selectedScopeAreas = React.useMemo(
        () => scopeAreaSelectOptions.filter(option => (formData?.scope_areas || []).includes(option.value)),
        [scopeAreaSelectOptions, formData?.scope_areas],
    );

    const fallbackProjectStatusOptions = React.useMemo(
        () => [
            { value: 'Discussing', text: 'Discussing' },
            { value: 'Scoping', text: 'Scoping' },
            { value: 'Proposal Sent', text: 'Proposal Sent' },
            { value: 'Active', text: 'Active' },
            { value: 'Report Issued', text: 'Report Issued' },
            { value: 'Closed', text: 'Closed' },
            { value: 'Invoicing', text: 'Invoicing' },
            { value: 'On Hold', text: 'On Hold' },
            { value: 'Lost', text: 'Lost' },
        ],
        [],
    );

    const projectStatusOptions = React.useMemo(() => {
        const opts = activeTenant?.project_status_options;
        if (Array.isArray(opts) && opts.length) {
            return opts
                .filter(o => o && o.name)
                .map(o => ({
                    value: o.name,
                    text: o.name,
                    color: o.color,
                }));
        }
        return fallbackProjectStatusOptions;
    }, [activeTenant, fallbackProjectStatusOptions]);

    const handleSubmit = async e => {
        e.preventDefault();
        const { customer_stage: _customerStage, ...projectFormData } = formData;
        const submitData = {
            ...projectFormData,
            project_deadline: format(new Date(formData?.project_deadline), 'dd-MM-yyyy'),
            project_manager: formData?.project_manager || null,
            lead_consultant: formData?.lead_consultant || null,
        };
        dispatch(
            PatchProjectData({
                data: submitData,
                id: selectedProject?.id,
            }),
        );
    };

    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleScopeAreasChange = selectedOptions => {
        setFormData(prev => ({
            ...prev,
            scope_areas: (selectedOptions || []).map(option => option.value),
        }));
    };

    const handleSetFormValues = React.useCallback(() => {
        setFormData({
            project_name: selectedProject?.project_name || '',
            investor_name: selectedProject?.investor_name || '',
            project_deadline: selectedProject?.project_deadline || '',
            project_type: selectedProject?.project_type || '',
            status: selectedProject?.status || '',
            engagement_objective: selectedProject?.engagement_objective || '',
            comments: selectedProject?.comments || '',
            project_manager: selectedProject?.project_manager || '',
            lead_consultant: selectedProject?.lead_consultant || '',
            support_consultants: selectedProject?.support_consultants || [],
            key_investor_name: selectedProject?.key_investor_name || '',
            key_investor_email: selectedProject?.key_investor_email || '',
            investment_type: selectedProject?.investment_type || '',
            customer_type: selectedProject?.customer_type || '',
            scope_areas: Array.isArray(selectedProject?.scope_areas) ? selectedProject.scope_areas : [],
        });
    }, [selectedProject]);

    React.useEffect(() => {
        if (selectedProject) {
            handleSetFormValues();
        }
    }, [selectedProject, handleSetFormValues]);

    return (
        <div className={styles.componentCard}>
            <h5 className={styles.componentTitle + ' mt-0'}>Project details</h5>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>
                        Project Name <span className={styles.required}>*</span>
                    </p>
                    <Input className={styles.inputField} required value={formData?.project_name} onChange={handleChange} name="project_name" />
                </div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Investor Name</p>
                    <Input className={styles.inputField} required value={formData?.investor_name} onChange={handleChange} name="investor_name" />
                </div>

                <div className="d-flex gap-3 mb-4">
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>Key Investor Name</p>
                        <Input
                            className={styles.inputField}
                            value={formData?.key_investor_name}
                            onChange={handleChange}
                            name="key_investor_name"
                            placeholder="Enter investor name"
                        />
                    </div>
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>Key Investor Email</p>
                        <Input
                            className={styles.inputField}
                            value={formData?.key_investor_email}
                            onChange={handleChange}
                            name="key_investor_email"
                            type="email"
                            placeholder="Enter investor email"
                        />
                    </div>
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>
                        Investment Type <span className={styles.required}>*</span>
                    </p>
                    <Select className={styles.selectField} required value={formData?.investment_type} onChange={handleChange} name="investment_type">
                        {investmentTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.text}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="d-flex gap-3 mb-4">
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>Status</p>
                        <Select className={styles.selectField} value={formData?.status} onChange={e => setFormData({ ...formData, status: e.target.value })} name="status">
                            <option value="">Select Status</option>
                            {projectStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>
                            Project Deadline <span className={styles.required}>*</span>
                        </p>
                        <Input className={styles.inputField} required type="date" value={formData?.project_deadline} onChange={handleChange} name="project_deadline" />
                    </div>
                </div>

                <div className="d-flex gap-3 mb-4">
                    <div className="flex-grow-1">
                        <p className={styles.fieldLabel}>
                            Project Type <span className={styles.required}>*</span>
                        </p>
                        <Select
                            className={styles.selectField}
                            required
                            value={formData?.project_type}
                            onChange={e => setFormData({ ...formData, project_type: e.target.value })}
                            name="project_type">
                            <option value="">Select Project Type</option>
                            {projectTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.text}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>
                        Customer Type <span className={styles.required}>*</span>
                    </p>
                    <Select className={styles.selectField} required value={formData?.customer_type} onChange={handleChange} name="customer_type">
                        {customerTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.text}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Scope Areas</p>
                    <ReactSelect
                        isMulti
                        closeMenuOnSelect={false}
                        options={scopeAreaSelectOptions}
                        value={selectedScopeAreas}
                        onChange={handleScopeAreasChange}
                        placeholder="Select scope areas"
                        styles={PROJECT_SCOPE_SELECT_STYLES}
                        classNamePrefix="projectScopeSelect"
                    />
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Engagement Objective</p>
                    <Textarea
                        className={styles.textareaField}
                        value={formData?.engagement_objective}
                        onChange={handleChange}
                        name="engagement_objective"
                        rows={8}
                        placeholder="Describe the engagement objectives for this project"
                    />
                </div>
                <div className={styles.divider}></div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Comments</p>
                    <Textarea className={styles.textareaField} value={formData?.comments} onChange={handleChange} name="comments" rows={4} />
                </div>

                <div className={styles.divider}></div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Project Manager</p>
                    <ReactSelect
                        className={styles.selectField}
                        value={
                            projectUsersAll?.find(item => item?.value?.group_member_id === formData?.project_manager)
                                ? {
                                      value: formData?.project_manager,
                                      label: projectUsersAll?.find(item => item?.value?.group_member_id === formData?.project_manager)?.value?.full_name,
                                  }
                                : null
                        }
                        onChange={selectedOption => setFormData({ ...formData, project_manager: selectedOption?.value || null })}
                        options={projectUsersAll?.map(item => ({
                            value: item?.value?.group_member_id,
                            label: item?.value?.full_name,
                        }))}
                        isClearable
                        placeholder="Select Project Manager"
                    />
                </div>
                <div className="mb-4">
                    <p className={styles.fieldLabel}>Lead Consultant</p>
                    <ReactSelect
                        className={styles.selectField}
                        value={
                            projectUsersAll?.find(item => item?.value?.group_member_id === formData?.lead_consultant)
                                ? {
                                      value: formData?.lead_consultant,
                                      label: projectUsersAll?.find(item => item?.value?.group_member_id === formData?.lead_consultant)?.value?.full_name,
                                  }
                                : null
                        }
                        onChange={selectedOption => setFormData({ ...formData, lead_consultant: selectedOption?.value || null })}
                        options={projectUsersAll
                            ?.filter(item => !formData?.support_consultants?.includes(item?.value?.group_member_id))
                            ?.map(item => ({
                                value: item?.value?.group_member_id,
                                label: item?.value?.full_name,
                            }))}
                        isClearable
                        placeholder="Select Lead Consultant"
                    />
                </div>

                <div className="mb-4">
                    <p className={styles.fieldLabel}>Support Consultants</p>
                    <ReactSelect
                        className={styles.selectField}
                        value={formData?.support_consultants
                            ?.map(consultantId => {
                                const consultant = projectUsersAll?.find(item => item?.value?.group_member_id === consultantId);
                                return consultant
                                    ? {
                                          value: consultantId,
                                          label: consultant?.value?.full_name,
                                      }
                                    : null;
                            })
                            .filter(Boolean)}
                        onChange={selectedOptions =>
                            setFormData({
                                ...formData,
                                support_consultants: selectedOptions?.map(option => option.value) || [],
                            })
                        }
                        options={projectUsersAll
                            ?.filter(item => item?.value?.group_member_id !== formData?.lead_consultant)
                            ?.map(item => ({
                                value: item?.value?.group_member_id,
                                label: item?.value?.full_name,
                            }))}
                        isMulti
                        isClearable
                        placeholder="Select Support Consultants"
                    />
                </div>

                <div className={styles.buttonDiv}>
                    {!compareObjectsValues(selectedProject, formData) && (
                        <div className="d-flex align-items-center justify-content-between">
                            <button
                                disabled={
                                    !formData?.project_name ||
                                    !formData?.project_deadline ||
                                    !formData?.project_type ||
                                    !formData?.investment_type ||
                                    !formData?.customer_type
                                }
                                type="submit"
                                className={styles.submitButton}>
                                Save
                                {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                            </button>
                            <span onClick={handleSetFormValues} className={styles.linkBtn}>
                                Revert
                            </span>
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default AddProject;
